import { Directive, Input, OnInit, OnDestroy, ComponentRef, ElementRef } from "@angular/core";
import { DsTooltipComponent } from "../ds-tooltip/ds-tooltip.component";
import { Positions, TooltipPositionConfig } from "../types/tooltips.types";
import { Overlay, OverlayPositionBuilder, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";

@Directive({
  selector: "[dsTooltip]",
  standalone: true,
})
export class DsTooltipDirective implements OnInit, OnDestroy {
  @Input("dsTooltip") message = "";
  @Input("dsTooltipPosition") position: Positions = "above";
  positionAbove = {
    originX: "center" as const,
    originY: "top" as const,
    overlayX: "center" as const,
    overlayY: "bottom" as const,
    offsetY: -8,
  };
  positionBelow = {
    originX: "center" as const,
    originY: "bottom" as const,
    overlayX: "center" as const,
    overlayY: "top" as const,
    offsetY: 8,
  };
  positionLeft = {
    originX: "start" as const,
    originY: "center" as const,
    overlayX: "end" as const,
    overlayY: "center" as const,
    offsetX: -8,
  };
  positionRight = {
    originX: "end" as const,
    originY: "center" as const,
    overlayX: "start" as const,
    overlayY: "center" as const,
    offsetX: 8,
  };
  private overlayRef: OverlayRef | null = null;
  private tooltipRef: ComponentRef<DsTooltipComponent> | null = null;
  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef
  ) {}
  ngOnInit() {
    this.elementRef.nativeElement.addEventListener("mouseenter", this.showTooltip);
    this.elementRef.nativeElement.addEventListener("mouseleave", this.hideTooltip);
    this.elementRef.nativeElement.addEventListener("touchstart", this.showTooltip);
    this.elementRef.nativeElement.addEventListener("touchend", this.hideTouchendTooltip);
  }
  ngOnDestroy() {
    this.hideTooltip();
    this.elementRef.nativeElement.removeEventListener("mouseenter", this.showTooltip);
    this.elementRef.nativeElement.removeEventListener("mouseleave", this.hideTooltip);
    this.elementRef.nativeElement.removeEventListener("touchstart", this.showTooltip);
    this.elementRef.nativeElement.removeEventListener("touchend", this.hideTouchendTooltip);
  }
  showTooltip = () => {
    if (this.overlayRef || !this.message) {
      return;
    }
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.getPositionConfig(this.position))
      .withPush(false)
      .withFlexibleDimensions(false);
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      hasBackdrop: false,
    });
    const tooltipPortal = new ComponentPortal(DsTooltipComponent);
    this.tooltipRef = this.overlayRef.attach(tooltipPortal);
    this.tooltipRef.instance.message = this.message;
    setTimeout(() => {
      if (this.tooltipRef) {
        this.tooltipRef.instance.animationState = "visible";
      }
    }, 0);
  };
  hideTooltip = () => {
    if (this.overlayRef && this.tooltipRef) {
      this.tooltipRef.instance.animationState = "hidden";
      setTimeout(() => {
        this.overlayRef?.detach();
        this.overlayRef?.dispose();
        this.overlayRef = null;
        this.tooltipRef = null;
      }, 300);
    }
  };

  hideTouchendTooltip = () => {
    setTimeout(() => {
      this.hideTooltip();
    }, 2000);
  };

  private getPositionConfig(position: Positions): TooltipPositionConfig[] {
    switch (position) {
      case "above":
        return [this.positionAbove, this.positionBelow];
      case "below":
        return [this.positionBelow, this.positionAbove];
      case "before":
      case "left":
        return [this.positionLeft, this.positionRight];
      case "after":
      case "right":
        return [this.positionRight, this.positionLeft];
      default:
        return [this.positionAbove, this.positionBelow];
    }
  }
}
